// /devices/:id/live — WebRTC 即時影像（spec 15；移植 legacy webapp 已驗證流程）。
//
// 時序（spec 15 §1.1，LOCKED；與 legacy app.js startViewing/onViewAnswer 完全一致）：
//   1. new RTCPeerConnection()         ← iceServers 留空（TURN 由 signaling 鑄造派發，client 永不自取）
//   2. createOffer()                   ← 先「不」setLocalDescription
//   3. start_viewing{sdp: offer}       ← Signaling WSS command（30s timeout）
//   4. response{session_id, ice_servers, sdp: answer}
//   5. setConfiguration({iceServers})  ← 套用派發的 ice_servers，gather 才會帶 TURN relay
//   6. setLocalDescription(offer) → setRemoteDescription(answer)
//   7. trickle ICE：本地 candidate 走頂層 {type:'ice_candidate'} frame；遠端經 ws.ts 'ice_candidate' 事件
//
// ICE 失敗恢復：spec 15 §5 階段C 的預算（2 次、間隔 3s）。瀏覽器 restartIce() 需要
// 重新協商通道（v3 start_viewing 無 renegotiate 語意），故以「重建 viewing session
// （重新 start_viewing 取新 ice_servers）」實作恢復——這正是 §5 唯一允許的重取路徑。

import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import type { SignalFrame, SignalingState } from '../../lib/ws';
import { SignalingTimeoutError, signaling } from '../../lib/ws';
import { Illustration } from '../../components/Illustration';
import { CaptureControls } from '../capture/CaptureControls';
import { PipCastControls } from '../pip/PipCastControls';
import { LiveZoomControls } from './LiveZoomControls';
import { selectedCandidatePair } from './iceMethod';
import { PtzPad } from './PtzPad';
import { LiveControlBar } from './LiveControlBar';
import {
  IDENTITY,
  isZoomed,
  nextFit,
  pan,
  toTransform,
  withZoom,
  zoomBy,
  ZOOM_STEP,
  WHEEL_STEP,
  type FitMode,
  type ZoomState,
} from './liveZoom';
import './devices.css';

type Phase = 'idle' | 'wss' | 'offer' | 'await' | 'negotiate' | 'streaming' | 'waking' | 'retrying' | 'stopped' | 'failed';

export interface LiveError {
  kind: 'device_offline' | 'device_waking' | 'max_sessions' | 'forbidden' | 'session_not_found' | 'timeout' | 'ice_failed' | 'unsupported' | 'generic';
  title: string;
  detail?: string;
  retryAfterMs?: number;
  /**
   * Raw 內部錯誤（英文 wire message / code，如 "signaling not connected"）。
   * W2-06：僅供 console / DOM title 屬性 / 觀測（spec 13），**絕不**直接
   * 渲染為 zh-TW UI 文案。
   */
  debug?: string;
}

const MAX_AUTO_RETRY = 2; // spec 15 §5 階段C：恢復預算 2 次
const MAX_WAKE_RETRY = 12; // APP#29：低功耗喚醒最多約 1 分鐘（預設 5s cadence）
const RETRY_DELAY_MS = 2000; // teardown 後重啟 viewing 的間隔（即時影像偏好快恢復）
const DISCONNECT_GRACE_MS = 3000; // spec 15 §5：disconnected 先給自癒寬限,逾時才重建（即時影像偏好快恢復）
const WSS_OPEN_TIMEOUT_MS = 20000;
const DEVICE_WAKE_RETRY_DEFAULT_MS = 5000;
const DEVICE_WAKE_RETRY_MIN_MS = 1000;
const DEVICE_WAKE_RETRY_MAX_MS = 30000;

// i18n：原為模組頂層 const map，改成 function 以利切語言即時重算。
// streaming / failed 無文案（空字串）→ 不進 i18n。
const PHASES_WITH_TEXT = new Set<Phase>(['idle', 'wss', 'offer', 'await', 'negotiate', 'waking', 'retrying', 'stopped']);
function phaseText(phase: Phase): string {
  return PHASES_WITH_TEXT.has(phase) ? i18n.t(`devices.phase_${phase}`) : '';
}

function numericFrameValue(frame: SignalFrame | undefined, key: string): number | null {
  const raw = frame?.[key] ?? frame?.payload?.[key];
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
  return Number.isFinite(n) ? n : null;
}

export function deviceWakeRetryAfterMs(frame: SignalFrame | undefined): number {
  const raw = numericFrameValue(frame, 'retry_after_ms');
  if (raw == null) return DEVICE_WAKE_RETRY_DEFAULT_MS;
  return Math.min(DEVICE_WAKE_RETRY_MAX_MS, Math.max(DEVICE_WAKE_RETRY_MIN_MS, Math.round(raw)));
}

/** 麥克風不能用的兩種原因。`nodevice` 不該叫使用者去開權限——他去了也沒有東西可以開。 */
export type MicBlockReason = 'denied' | 'nodevice';

/**
 * getUserMedia 的失敗 → 使用者看得懂的兩類。
 * 只讀 `DOMException.name`（機器產物）；認不得就當「被拒」——那是遠比「沒有麥克風」常見的情況，
 * 而且它的指示（去網站權限看一下）對兩種情形都不會造成傷害。
 *
 * ⚠️ 不可以用 `e instanceof Error` 取 name：`DOMException` **不是** Error 的實例（jsdom 如此，
 * 舊版瀏覽器亦然），而 getUserMedia 拋的就是 DOMException —— 用 instanceof 會讓每一種原因
 * 都掉進 `denied`，包含「這台機器根本沒有麥克風」。那正是本函式要分開的兩件事。
 * (寫測試時實際踩到:兩個 nodevice 案例全紅,denied 的三個則是「碰巧對」。)
 */
export function micBlockReason(e: unknown): MicBlockReason {
  const name =
    typeof e === 'object' && e !== null && 'name' in e ? String((e as { name?: unknown }).name ?? '') : '';
  return name === 'NotFoundError' || name === 'OverconstrainedError' ? 'nodevice' : 'denied';
}

export function classifyError(e: unknown): LiveError {
  if (e instanceof SignalingTimeoutError) {
    return { kind: 'timeout', title: i18n.t('devices.errTimeoutTitle'), detail: i18n.t('devices.errTimeoutDetail'), debug: e.message };
  }
  const frame = (e as { frame?: SignalFrame }).frame;
  const payload = (frame?.payload ?? {}) as { code?: string };
  // 錯誤碼來源三層：error frame 的頂層 code → payload.code → response frame 的
  // 非 ok status（v3-dev 實測 emu 裝置回 {type:'response', status:'unsupported_command'}，W4）。
  const status = typeof frame?.status === 'string' && frame.status !== 'ok' ? frame.status : '';
  const code = String(frame?.['code'] ?? payload.code ?? status);
  // W2-06：raw 內部字串（e.message / wire code，多為英文）一律降級到 debug
  // （console / title 屬性），不得作為 zh-TW UI 文案直接面對使用者。
  const debug = e instanceof Error ? e.message : code || undefined;
  // ⚠️ 觀看數已滿有**兩道不同的閘、兩種不同的形狀**，只認 code 會漏掉實際上最常發生的那一道：
  //   ① 雲端閘（先觸發）：signaling `websocket.go` 的 `room.SessionCount() >= d.maxViewers()`
  //      → `error{code:"forbidden", message:"device viewer limit reached"}`
  //      ⇒ 只看 code 會講成「你沒有此裝置的即時影像觀看權限」，那會叫使用者去找分享者要權限，
  //        而他其實只要等一下。**六類錯誤裡最可行動的一類，被講成最不可行動的那一類。**
  //   ② 裝置閘：韌體自己的 session 上限 → response 頂層 `status:"max_sessions_reached"`
  //      （device `wire_schema.h` LS_ST_MAX_SESSIONS → `pc_ipro85.c` → `test_media_seam.c` 釘住 wire 形）
  // 兩條都要接。iOS/Android 的分類器早就有這條字串比對，web 先前沒有。
  const message = e instanceof Error ? e.message.toLowerCase() : '';
  if (message.includes('viewer limit')) {
    return { kind: 'max_sessions', title: i18n.t('devices.errMaxSessionsTitle'), detail: i18n.t('devices.errMaxSessionsDetail'), debug };
  }
  switch (code) {
    case 'device_waking': {
      const retryAfterMs = deviceWakeRetryAfterMs(frame);
      return {
        kind: 'device_waking',
        title: i18n.t('devices.errDeviceWakingTitle'),
        detail: i18n.t('devices.errDeviceWakingDetail', { seconds: Math.ceil(retryAfterMs / 1000) }),
        retryAfterMs,
        debug,
      };
    }
    case 'device_offline':
    case 'device_not_found':
      return { kind: 'device_offline', title: i18n.t('devices.errOfflineTitle'), detail: i18n.t('devices.errOfflineDetail'), debug };
    case 'max_sessions_reached':
      return { kind: 'max_sessions', title: i18n.t('devices.errMaxSessionsTitle'), detail: i18n.t('devices.errMaxSessionsDetail'), debug };
    case 'forbidden':
      return { kind: 'forbidden', title: i18n.t('devices.errForbiddenTitle'), detail: i18n.t('devices.errForbiddenDetail'), debug };
    case 'session_not_found':
      return { kind: 'session_not_found', title: i18n.t('devices.errSessionNotFoundTitle'), detail: i18n.t('devices.errSessionNotFoundDetail'), debug };
    case 'unsupported_command':
      // control-plane-only 裝置（如 emu）不支援媒體：終態、不提供重試（spec 15 §6.4）
      return { kind: 'unsupported', title: i18n.t('devices.errUnsupportedTitle'), detail: i18n.t('devices.errUnsupportedDetail'), debug };
    default:
      // 未知錯誤：通用中文文案 + 重試；raw 訊息只進 debug（W2-06——舊版把
      // "signaling not connected"/"signaling disconnected" 直接顯示在 zh-TW UI）。
      return { kind: 'generic', title: i18n.t('devices.errGenericTitle'), detail: i18n.t('devices.errGenericDetail'), debug };
  }
}

/** start_viewing response 的媒體協商素材（驗證後）。 */
export interface ViewingGrant {
  sessionId: string | null;
  iceServers: RTCIceServer[];
  answer: RTCSessionDescriptionInit;
}

/**
 * 驗證 start_viewing 的 type:'response' 回覆（W4/APP-02）：
 * - 頂層 status 非 'ok'（如 emu 的 'unsupported_command'）→ throw（帶 frame 供 classifyError）；
 *   絕不讓 UI 拿空 payload 進 NEGOTIATING 後懸置。
 * - status ok 但缺 sdp/answer → 同樣 throw（防衛：無 answer 無從協商，直接終態）。
 * Pure + exported 供單元測試。
 */
export function extractViewingGrant(resp: SignalFrame): ViewingGrant {
  const status = typeof resp.status === 'string' ? resp.status : 'ok';
  if (status !== 'ok') {
    const err = new Error(`start_viewing failed: ${status}`);
    (err as Error & { frame: SignalFrame }).frame = resp;
    throw err;
  }
  const p = (resp.payload ?? {}) as {
    session_id?: string;
    ice_servers?: RTCIceServer[];
    sdp?: string | RTCSessionDescriptionInit;
    answer?: string | RTCSessionDescriptionInit;
  };
  const raw = p.sdp ?? p.answer;
  if (!raw) {
    const err = new Error(i18n.t('live.missingSdpAnswer'));
    (err as Error & { frame: SignalFrame }).frame = resp;
    throw err;
  }
  return {
    sessionId: p.session_id ?? null,
    iceServers: Array.isArray(p.ice_servers) ? p.ice_servers : [],
    answer: typeof raw === 'string' ? { type: 'answer', sdp: raw } : raw,
  };
}

/** 等 signaling 進入 open（觸發 connect；backoff 由 ws.ts 內建）。 */
function waitForOpen(): Promise<void> {
  if (signaling.getState() === 'open') return Promise.resolve();
  signaling.connect();
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const off = signaling.on('state', (s) => {
      if (s === 'open') {
        off();
        if (timer) clearTimeout(timer);
        resolve();
      } else if (s === 'closed') {
        off();
        if (timer) clearTimeout(timer);
        reject(new Error(i18n.t('live.signalingClosed')));
      }
    });
    timer = setTimeout(() => {
      off();
      reject(new Error(i18n.t('live.signalingTimeout')));
    }, WSS_OPEN_TIMEOUT_MS);
  });
}

interface RtcRefs {
  gen: number;
  wantLive: boolean;
  active: boolean;
  pc: RTCPeerConnection | null;
  sessionId: string | null;
  pendingIce: RTCIceCandidateInit[];
  audioTx: RTCRtpTransceiver | null; // V4P-10 雙向對講 audio transceiver
  micStream: MediaStream | null;     // push-to-talk 麥克風(啟用時才取得)
  statsTimer: ReturnType<typeof setInterval> | null;
  retryTimer: ReturnType<typeof setTimeout> | null;
  discTimer: ReturnType<typeof setTimeout> | null;
  restarts: number;
  wakeRetries: number;
  last: { bytes: number; frames: number; ts: number };
  lastIceMethod: string | null; // 上次 selected pair 的 candidateType(變動才記 log,區分真在跳 vs 顯示雜訊)
}

export default function DeviceLivePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const deviceId = id ?? '';
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [talking, setTalking] = useState(false); // V4P-10 push-to-talk 狀態
  // 麥克風不能用的原因(被拒 / 沒有裝置);null=沒問題。見 toggleTalk 的 catch。
  const [micBlocked, setMicBlocked] = useState<'denied' | 'nodevice' | null>(null);
  const [liveStream, setLiveStream] = useState<MediaStream | null>(null); // APP#17 截圖/錄影來源
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<LiveError | null>(null);
  const [wsState, setWsState] = useState<SignalingState>(signaling.getState());
  const [retryCount, setRetryCount] = useState(0);
  const [iceUnstable, setIceUnstable] = useState(false); // disconnected 寬限期:畫面仍在但連線不穩 → 顯示 banner

  // 畫面比例 + 數位變焦（APP#9，對齊 Vistaflow M4）。純前端 CSS transform，
  // 不影響串流/信令。pinch（雙指）/ 滾輪 / ＋－ 按鈕皆改 zoomState，放大後拖曳平移。
  const [zoomState, setZoomState] = useState<ZoomState>(IDENTITY);
  const [fit, setFit] = useState<FitMode>('contain');
  const stageRef = useRef<HTMLDivElement | null>(null);
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchRef = useRef<{ dist: number; zoom: number } | null>(null);

  const r = useRef<RtcRefs>({
    gen: 0,
    wantLive: false,
    active: true,
    pc: null,
    sessionId: null,
    pendingIce: [],
    audioTx: null,
    micStream: null,
    statsTimer: null,
    retryTimer: null,
    discTimer: null,
    restarts: 0,
    wakeRetries: 0,
    last: { bytes: 0, frames: 0, ts: 0 },
    lastIceMethod: null,
  });

  /** 本地 candidate → 頂層 ice_candidate frame（common/02；非 command；legacy 同款）。 */
  function sendIce(candidate: RTCIceCandidateInit): void {
    signaling.sendRaw({
      type: 'ice_candidate',
      session_id: r.current.sessionId ?? undefined,
      device_id: deviceId,
      candidate,
    } as SignalFrame);
  }

  /** 釋放 PeerConnection 與計時器；sendStop=true 時補送 stop_viewing（spec 15 STOPPING）。 */
  function teardownSession(sendStop: boolean): void {
    const s = r.current;
    if (s.statsTimer) {
      clearInterval(s.statsTimer);
      s.statsTimer = null;
    }
    if (s.retryTimer) {
      clearTimeout(s.retryTimer);
      s.retryTimer = null;
    }
    if (s.discTimer) {
      clearTimeout(s.discTimer);
      s.discTimer = null;
    }
    if (sendStop && s.sessionId) {
      // fire-and-forget：response 與否都不阻塞 UI
      signaling.request('stop_viewing', { session_id: s.sessionId }, { device_id: deviceId }).catch(() => {});
    }
    if (s.pc) {
      try {
        s.pc.close();
      } catch {
        /* already closed */
      }
    }
    if (s.micStream) {
      s.micStream.getTracks().forEach((t) => t.stop());
      s.micStream = null;
    }
    s.audioTx = null;
    s.pc = null;
    s.sessionId = null;
    s.pendingIce = [];
    s.last = { bytes: 0, frames: 0, ts: 0 };
    const v = videoRef.current;
    if (v) v.srcObject = null;
    setLiveStream(null); // APP#17：串流結束 → 錄影中的 CaptureControls 自動收尾落檔
  }

  /** ICE 失敗：預算內自動重建 session，否則進 FAILED（spec 15 §5）。 */
  function failOrRetry(why: LiveError): void {
    const s = r.current;
    if (!s.active || !s.wantLive) return;
    teardownSession(true);
    if (s.restarts < MAX_AUTO_RETRY) {
      s.restarts += 1;
      setRetryCount(s.restarts);
      setPhase('retrying');
      s.retryTimer = setTimeout(() => {
        s.retryTimer = null;
        void start();
      }, RETRY_DELAY_MS);
    } else {
      s.wantLive = false;
      setError(why);
      setPhase('failed');
    }
  }

  async function pollStats(): Promise<void> {
    const pc = r.current.pc;
    if (!pc) return;
    try {
      const report = await pc.getStats();
      const byId = new Map<string, Record<string, unknown>>();
      let inbound: Record<string, unknown> | null = null;
      report.forEach((entry) => {
        const rec = entry as unknown as Record<string, unknown>;
        byId.set(String(rec.id), rec);
        if (rec.type === 'inbound-rtp' && rec.kind === 'video') inbound = rec;
      });
      if (!inbound) return;
      // 只保留 ICE 路徑偵測 —— 位元率/FPS/解析度/封包遺失的唯一消費者(常駐統計卡)已移除,
      // 再算下去就是沒有讀者的計算。`inbound` 仍要取,因為沒有 inbound-rtp 就代表還沒真的在收。
      const pair = selectedCandidatePair(byId);
      const lc = pair ? byId.get(String(pair.localCandidateId)) : undefined;
      const iceMethod = lc?.candidateType != null ? String(lc.candidateType) : null;
      if (iceMethod !== r.current.lastIceMethod) {
        // selected pair 真的換了才記:區分「真 ICE 在跳」(裝置/網路側,帶 RTT 供 Scott pion 排查)
        // 與「顯示雜訊」(修掉後就不該再變)。
        const rtt = pair?.currentRoundTripTime;
        console.info(
          `[live] ice_method ${r.current.lastIceMethod ?? '∅'} → ${iceMethod ?? '∅'}`,
          rtt != null ? `rtt=${String(rtt)}s` : '',
        );
        r.current.lastIceMethod = iceMethod;
      }
    } catch {
      /* getStats 失敗不致命 */
    }
  }

  async function start(fromWakeRetry = false): Promise<void> {
    const s = r.current;
    if (!deviceId || !s.active) return;
    if (!fromWakeRetry) s.wakeRetries = 0;
    const gen = ++s.gen;
    s.wantLive = true;
    setError(null);
    teardownSession(false);
    try {
      if (signaling.getState() !== 'open') {
        setPhase('wss');
        await waitForOpen();
        if (gen !== s.gen || !s.active) return;
      }
      // room 訂閱不在這裡:單一訂閱點於 room-lifecycle effect(W8/APP-04——
      // start/重試不得重發 subscribe,首入 live 對同 device 僅一筆 wire frame)

      setPhase('offer');
      // iceServers 留空建立：TURN 由 start_viewing response 派發（LOCKED；spec 15 §1.1）
      const pc = new RTCPeerConnection();
      s.pc = pc;
      // video recvonly（device answer 以此為準）+ audio sendrecv（雙向對講,
      // V4P-10；recv=聽 device 麥、send=對講麥克風。spec 15 §1.1 v1）。
      pc.addTransceiver('video', { direction: 'recvonly' });
      r.current.audioTx = pc.addTransceiver('audio', { direction: 'sendrecv' });

      pc.ontrack = (ev) => {
        if (r.current.pc !== pc) return;
        if (ev.track.kind === 'audio') {
          // device→client 音訊：掛到隱藏 <audio> 播放(聽)。
          const a = audioRef.current;
          if (a) {
            a.srcObject = ev.streams[0] ?? new MediaStream([ev.track]);
            void a.play().catch(() => {});
          }
          return;
        }
        const stream = ev.streams[0] ?? new MediaStream([ev.track]);
        const v = videoRef.current;
        if (v) {
          v.srcObject = stream;
          void v.play().catch(() => {});
        }
        setLiveStream(stream); // APP#17：供截圖/錄影
        r.current.restarts = 0; // 成功取流 → 恢復預算歸零
        r.current.wakeRetries = 0;
        setRetryCount(0);
        setPhase('streaming');
        if (!r.current.statsTimer) r.current.statsTimer = setInterval(() => void pollStats(), 1000);
      };

      pc.onicecandidate = (ev) => {
        if (r.current.pc !== pc || !ev.candidate) return;
        const cand = ev.candidate.toJSON();
        if (r.current.sessionId) sendIce(cand);
        else r.current.pendingIce.push(cand); // session_id 未到前先排隊（legacy 同款）
      };

      pc.oniceconnectionstatechange = () => {
        if (r.current.pc !== pc) return;
        const st = pc.iceConnectionState;
        if (st === 'failed') {
          setIceUnstable(false);
          failOrRetry({ kind: 'ice_failed', title: t('devices.errIceFailedTitle'), detail: t('devices.errIceFailedDetail') });
        } else if (st === 'disconnected') {
          setIceUnstable(true); // 畫面可能還在,但連線不穩 → banner 提示(別讓使用者乾等沒回饋)
          if (!r.current.discTimer) {
            r.current.discTimer = setTimeout(() => {
              r.current.discTimer = null;
              if (r.current.pc === pc && (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed')) {
                setIceUnstable(false);
                failOrRetry({ kind: 'ice_failed', title: t('devices.errIceFailedTitle'), detail: t('devices.errIceTimeoutDetail') });
              }
            }, DISCONNECT_GRACE_MS);
          }
        } else if (st === 'connected' || st === 'completed') {
          setIceUnstable(false);
          if (r.current.discTimer) {
            clearTimeout(r.current.discTimer);
            r.current.discTimer = null;
          }
        }
      };

      const offer = await pc.createOffer();
      if (gen !== s.gen || s.pc !== pc) return;

      setPhase('await');
      const resp = await signaling.request('start_viewing', { sdp: offer }, { device_id: deviceId });
      if (gen !== s.gen || s.pc !== pc) return;

      // W4/APP-02：先驗 status 與 answer（非 ok / 缺 sdp → throw 進 catch 終態），
      // 不得帶著空 payload 進 NEGOTIATING 懸置。
      const grant = extractViewingGrant(resp);
      if (grant.sessionId) s.sessionId = grant.sessionId;
      setPhase('negotiate');

      // 順序固定：setConfiguration（派發 ice_servers）→ setLocalDescription → setRemoteDescription
      if (grant.iceServers.length > 0) {
        try {
          pc.setConfiguration({ iceServers: grant.iceServers });
        } catch {
          /* 無法套用時仍以 host/srflx 嘗試 */
        }
      }
      await pc.setLocalDescription(offer);
      await pc.setRemoteDescription(grant.answer);
      // flush：session_id 存在前排隊的本地 candidate
      const queued = s.pendingIce.splice(0);
      for (const c of queued) sendIce(c);
    } catch (e) {
      if (gen !== r.current.gen || !r.current.active) return;
      teardownSession(false);
      const le = classifyError(e);
      if (le.kind === 'device_waking') {
        if (s.wakeRetries >= MAX_WAKE_RETRY) {
          s.wantLive = false;
          setError({
            kind: 'timeout',
            title: i18n.t('devices.errDeviceWakeTimeoutTitle'),
            detail: i18n.t('devices.errDeviceWakeTimeoutDetail'),
            debug: le.debug,
          });
          setPhase('failed');
          return;
        }
        s.wakeRetries += 1;
        r.current.wantLive = true;
        // 低功耗喚醒由 server/device 處理；client 只依 retry_after_ms 重發 start_viewing。
        setRetryCount(s.wakeRetries);
        setError(le);
        setPhase('waking');
        r.current.retryTimer = setTimeout(() => {
          r.current.retryTimer = null;
          if (r.current.active && r.current.wantLive) void start(true);
        }, le.retryAfterMs ?? DEVICE_WAKE_RETRY_DEFAULT_MS);
        return;
      }
      r.current.wantLive = false; // start_viewing 被拒（offline/額滿/無權限…）→ 不自動重試
      // W2-06：raw 內部錯誤只進 console（觀測降級，spec 13）與 title 屬性
      if (le.debug) console.warn(`[live] ${deviceId} failed kind=${le.kind}:`, le.debug);
      setError(le);
      setPhase('failed');
    }
  }

  // 對講 push-to-talk(V4P-10):取麥克風 → 替換 audio sender track；放開 → 靜音。
  // device 端 audio transceiver=sendrecv,client send=麥克風、recv=device 麥(聽)。
  async function toggleTalk(): Promise<void> {
    const s = r.current;
    const tx = s.audioTx;
    if (!tx) return;
    if (talking) {
      if (s.micStream) {
        s.micStream.getTracks().forEach((t) => t.stop());
        s.micStream = null;
      }
      try {
        await tx.sender.replaceTrack(null);
      } catch {
        /* sender gone (teardown raced) */
      }
      setTalking(false);
      return;
    }
    try {
      setMicBlocked(null);
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (r.current.pc !== s.pc) {
        mic.getTracks().forEach((t) => t.stop());
        return; // session torn down while permission prompt was open
      }
      s.micStream = mic;
      await tx.sender.replaceTrack(mic.getAudioTracks()[0] ?? null);
      setTalking(true);
    } catch (e) {
      // 舊版是 `catch {}` —— 連錯誤物件都沒綁,按下「對講」之後畫面**什麼都不會發生**:
      // 按鈕彈回未按下、沒有訊息、沒有 console。而瀏覽器在使用者拒絕過之後不會再跳權限詢問,
      // 所以再按一次也是同樣的無聲無息 = 死路(三平台裡最糟的一個,iOS/Android 至少有提示)。
      // 分兩種:被拒(要去瀏覽器的網站權限開)vs 沒有麥克風(去設定也沒用,不要叫他白跑一趟)。
      setMicBlocked(micBlockReason(e));
      // 原文只進 console(與 classifyError 的 debug 同慣例:內部字串不上畫面)。
      console.warn(`[live] ${deviceId} microphone unavailable:`, e);
      setTalking(false); // 維持聆聽
    }
  }

  function stopViewing(): void {
    setMicBlocked(null); // banner 寫著「再按一次對講」,而對講鈕只在 streaming 時存在
    const s = r.current;
    s.wantLive = false;
    s.gen += 1;
    s.restarts = 0;
    s.wakeRetries = 0;
    setRetryCount(0);
    teardownSession(true);
    setPhase('stopped');
  }

  // 掛載：開始觀看；卸載：stop_viewing + 釋放（spec 15 STOPPING）
  useEffect(() => {
    const s = r.current;
    s.active = true;
    void start();
    return () => {
      s.active = false;
      s.gen += 1;
      s.wantLive = false;
      teardownSession(true);
    };
    // start/teardown 走 refs，僅依 deviceId 重跑
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  // room 生命週期（W8/APP-04 單一訂閱點）：掛載時已 open 訂一次；之後僅在
  // 'open' 轉換（初連/重連，server 房籍隨連線消失）由 state listener 重訂；
  // 卸載退訂一次。ws.ts subscribe 另有 per-connection 冪等+refcount 兜底
  // （與 events feed 等其他訂戶共 room 不互踩）。
  useEffect(() => {
    if (signaling.getState() === 'open') void signaling.subscribe(deviceId).catch(() => {});
    return () => {
      void signaling.unsubscribe(deviceId).catch(() => {});
    };
  }, [deviceId]);

  // 遠端 ICE（device candidate；頂層或 command 形 ws.ts 都會路由到這）
  useEffect(() => {
    const off = signaling.on('ice_candidate', (frame) => {
      const s = r.current;
      const pc = s.pc;
      if (!pc) return;
      const fdev = frame.device_id ?? (frame.payload as { device_id?: string } | undefined)?.device_id;
      if (fdev && fdev !== deviceId) return;
      const fsess = frame.session_id ?? (frame.payload as { session_id?: string } | undefined)?.session_id;
      if (fsess && s.sessionId && fsess !== s.sessionId) return;
      const cand = (frame.payload as { candidate?: unknown } | undefined)?.candidate ?? frame['candidate'];
      if (!cand || cand === 'null') return; // null/'null' = end-of-candidates
      void pc.addIceCandidate(cand as RTCIceCandidateInit).catch(() => {});
    });
    return off;
  }, [deviceId]);

  // 信令狀態：WSS 斷但媒體活著 → 照播只示警；媒體已死且仍想看 → 重連後重建 session（spec 15 §4）
  useEffect(() => {
    const off = signaling.on('state', (sNew) => {
      setWsState(sNew);
      if (sNew !== 'open') return;
      const s = r.current;
      if (!s.active) return;
      void signaling.subscribe(deviceId).catch(() => {});
      const mediaDead = !s.pc || s.pc.connectionState === 'failed' || s.pc.connectionState === 'closed';
      if (s.wantLive && mediaDead && !s.retryTimer) void start();
    });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const busy = phase === 'wss' || phase === 'offer' || phase === 'await' || phase === 'negotiate' || phase === 'waking' || phase === 'retrying' || phase === 'idle';

  // ---- 數位變焦互動（APP#9）----
  // 離開 streaming（停止/失敗/重連建立中）即回正:避免下次連上殘留縮放。
  useEffect(() => {
    if (phase !== 'streaming') {
      setZoomState(IDENTITY);
      setFit('contain');
    }
  }, [phase]);

  // 滾輪縮放:用原生非被動監聽以可 preventDefault（避免頁面捲動）。
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (phase !== 'streaming') return;
      e.preventDefault();
      setZoomState((z) => zoomBy(z, e.deltaY < 0 ? WHEEL_STEP : -WHEEL_STEP));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [phase]);

  const stageDims = () => {
    const el = stageRef.current;
    return { w: el?.clientWidth || 1, h: el?.clientHeight || 1 };
  };

  const onStagePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (phase !== 'streaming') return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, zoom: zoomState.zoom };
    }
  };

  const onStagePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const prev = pointersRef.current.get(e.pointerId);
    if (!prev) return;
    const cur = { x: e.clientX, y: e.clientY };
    pointersRef.current.set(e.pointerId, cur);
    if (pointersRef.current.size >= 2 && pinchRef.current) {
      const [a, b] = [...pointersRef.current.values()];
      const factor = Math.hypot(a.x - b.x, a.y - b.y) / pinchRef.current.dist;
      const base = pinchRef.current.zoom;
      setZoomState((z) => withZoom(z, base * factor));
      return;
    }
    // 單指拖曳:放大時平移
    setZoomState((z) => {
      if (!isZoomed(z)) return z;
      const { w, h } = stageDims();
      return pan(z, (cur.x - prev.x) / w, (cur.y - prev.y) / h);
    });
  };

  const onStagePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
  };

  return (
    <section>
      <header className="lv-header">
        <div>
          <h1>{t('devices.liveVideo')}</h1>
          <p className="text-secondary" style={{ margin: 0 }}>
            {deviceId}
          </p>
        </div>
        <div className="lv-actions">
          <Link className="btn-ghost" to={`/devices/${encodeURIComponent(deviceId)}`}>
            {t('devices.backToDevice')}
          </Link>
          {phase === 'streaming' && (
            <button
              className={talking ? 'btn-primary' : 'btn-ghost'}
              onClick={() => void toggleTalk()}
              data-testid="talk-toggle"
              aria-pressed={talking}
            >
              {/* click-toggle(非按住);emoji 裝飾 aria-hidden,文案改點擊結束（a11y review） */}
              <span aria-hidden="true">🎙</span> {talking ? t('devices.talkingTapToEnd') : t('devices.talk')}
            </button>
          )}
          {phase === 'streaming' && (
            <CaptureControls
              videoRef={videoRef}
              mediaStream={liveStream}
              deviceName={deviceId}
              deviceId={deviceId}
              ready={phase === 'streaming' && !!liveStream}
            />
          )}
          {phase === 'streaming' && (
            <PipCastControls videoRef={videoRef} ready={phase === 'streaming' && !!liveStream} />
          )}
          {phase === 'streaming' && (
            <LiveZoomControls
              zoom={zoomState.zoom}
              fit={fit}
              onZoomIn={() => setZoomState((z) => zoomBy(z, ZOOM_STEP))}
              onZoomOut={() => setZoomState((z) => zoomBy(z, -ZOOM_STEP))}
              onReset={() => setZoomState(IDENTITY)}
              onToggleFit={() => setFit((f) => nextFit(f))}
            />
          )}
          {phase === 'streaming' || busy ? (
            <button className="btn-ghost" onClick={stopViewing}>
              {t('devices.stopViewing')}
            </button>
          ) : (
            <button className="btn-primary" onClick={() => void start()}>
              {t('devices.reconnect')}
            </button>
          )}
        </div>
      </header>

      {micBlocked !== null && (
        <div className="lv-banner" role="status" data-testid="mic-blocked-banner">
          {micBlocked === 'nodevice' ? t('devices.micNoDevice') : t('devices.micDenied')}{' '}
          <button className="btn-ghost" onClick={() => setMicBlocked(null)} data-testid="mic-blocked-dismiss">
            {t('devices.micDismiss')}
          </button>
        </div>
      )}
      {wsState === 'reconnecting' && phase === 'streaming' && <div className="lv-banner">{t('devices.signalingReconnecting')}</div>}
      {iceUnstable && phase === 'streaming' && <div className="lv-banner">{t('devices.iceRecovering')}</div>}

      <div
        className={`lv-stage${isZoomed(zoomState) ? ' lv-zoomed' : ''}`}
        ref={stageRef}
        onPointerDown={onStagePointerDown}
        onPointerMove={onStagePointerMove}
        onPointerUp={onStagePointerUp}
        onPointerCancel={onStagePointerUp}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ objectFit: fit, transform: toTransform(zoomState) }}
        />
        {/* 變焦倍率角標（APP#9）*/}
        {phase === 'streaming' && isZoomed(zoomState) && (
          <span className="lv-zoom-badge" data-testid="live-zoom-badge">
            {zoomState.zoom.toFixed(1)}×
          </span>
        )}
        <audio ref={audioRef} autoPlay />
        {phase !== 'streaming' && (
          <div className="lv-overlay">
            {phase === 'failed' && error ? (
              <>
                {/* 裝置離線:配品牌插畫(APP 美化材料) */}
                {error.kind === 'device_offline' && <Illustration name="offline" alt="" size={160} />}
                {/* W2-06：raw 內部錯誤僅入 title 屬性（hover 供除錯），不渲染為文案 */}
                <p className="lv-err-title" title={error.debug}>
                  {error.title}
                </p>
                {error.detail && (
                  <p className="text-secondary" style={{ maxWidth: 420 }}>
                    {error.detail}
                  </p>
                )}
                {/* unsupported = 裝置能力性終態（如 emu control-plane only），重試無意義 */}
                {error.kind !== 'unsupported' ? (
                  <button className="btn-primary" onClick={() => void start()}>
                    {t('common.retry')}
                  </button>
                ) : (
                  <Link className="btn-primary" to={`/devices/${encodeURIComponent(deviceId)}`}>
                    {t('devices.backToDevice')}
                  </Link>
                )}
              </>
            ) : phase === 'stopped' ? (
              <>
                <p className="lv-err-title">{t('devices.phase_stopped')}</p>
                <button className="btn-primary" onClick={() => void start()}>
                  {t('devices.reconnect')}
                </button>
              </>
            ) : (
              <>
                <div className="lv-spin" aria-hidden="true" />
                <p>{phaseText(phase)}</p>
                {phase === 'retrying' && (
                  <p className="text-secondary">
                    {t('devices.autoRetryProgress', { current: retryCount, max: MAX_AUTO_RETRY })}
                  </p>
                )}
                {phase === 'waking' && error?.detail && (
                  <>
                    <p className="text-secondary" style={{ maxWidth: 420 }}>
                      {error.detail}
                    </p>
                    <p className="text-secondary">
                      {t('devices.wakeRetryProgress', { current: retryCount, max: MAX_WAKE_RETRY })}
                    </p>
                  </>
                )}
              </>
            )}
          </div>
        )}
        {phase === 'streaming' && <span className="lv-live-badge">LIVE</span>}
      </div>

      {/* in-live 控制列快捷（APP#41, spec 15 §11）— 警笛/燈/追蹤/手動事件/畫質。
          LiveControlBar 自取 capability + role，只渲染命中且 owner/admin 可控的快捷；
          串流中（ready=streaming）才可操作。 */}
      <LiveControlBar deviceId={deviceId} ready={phase === 'streaming'} />

      {/* PTZ 方向盤 + Zoom（APP#11, spec 21 §3.8）— 僅 PTZ-capable 裝置渲染（PtzPad 自取 capability）；
          串流中才可操作（ready=streaming），非串流時 disable。 */}
      <PtzPad deviceId={deviceId} ready={phase === 'streaming'} />

      {/* 常駐串流統計（位元率 / FPS / 解析度 / 封包遺失 / 路徑）已於 2026-08-20 移除。
          iOS/Android 已在 08-19 同批移除,web 是最後一個 —— 理由相同:**這是使用 App,不是 debug App**。
          看畫面的人不需要知道 kbps,而那五個數字每秒重繪一次,是頁面上唯一會自己動的東西。
          診斷沒有因此消失:`pollStats` 仍在跑,ICE 路徑換手時照樣寫 `console.info('[live] ice_method …')`
          (帶 RTT,供 pion 側排查),只是不再佔用使用者的版面。 */}
    </section>
  );
}
